import { useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'

export type SegmentedTab<K extends string> = { key: K; label: string }

type SegmentedTabsProps<K extends string> = {
  tabs: readonly SegmentedTab<K>[]
  active: K
  onSelect: (key: K) => void
  ariaLabel: string
  /** Prefisso degli id delle tab (`<idPrefix>-<key>`), a cui punta l'`aria-labelledby` del pannello. */
  idPrefix: string
  panelId: string
  /** `data-testid` = `<testIdPrefix><key>` (es. `tab-group`, `run-tab-matches`). */
  testIdPrefix?: string
  /** `paper` sulle pagine chiare, `night` sulle schermate partita. */
  tone?: 'paper' | 'night'
  /** Poche voci (2-3): la pista occupa tutta la larghezza e le voci si dividono lo spazio. */
  fill?: boolean
  className?: string
}

const TONES = {
  paper: {
    track: 'border border-line bg-paper-deep',
    active: 'bg-ink text-paper shadow-card',
    idle: 'text-ink-soft hover:bg-ink/5 hover:text-ink',
  },
  night: {
    track: 'border border-paper/10 bg-paper/5',
    active: 'bg-paper text-ink',
    idle: 'text-paper/65 hover:bg-paper/10 hover:text-paper',
  },
}

/**
 * Tab a segmenti: una pista che scorre in orizzontale fino al bordo dello schermo su mobile (niente tab
 * tagliate a metà senza saperlo), voce attiva piena come le palette del tabellone e sempre portata in vista.
 * Il genitore decide dove sta (di solito in una fascia sticky) e rende il pannello con `id={panelId}`.
 */
export function SegmentedTabs<K extends string>({
  tabs,
  active,
  onSelect,
  ariaLabel,
  idPrefix,
  panelId,
  testIdPrefix = 'tab-',
  tone = 'paper',
  fill = false,
  className,
}: SegmentedTabsProps<K>) {
  const trackRef = useRef<HTMLDivElement>(null)
  const styles = TONES[tone]

  // Scorre solo la pista (non la pagina, come farebbe scrollIntoView) per mostrare la tab attiva.
  useEffect(() => {
    const track = trackRef.current
    const button = track?.querySelector<HTMLElement>('[aria-selected="true"]')
    if (!track || !button) return
    const left = button.offsetLeft - track.clientWidth / 2 + button.offsetWidth / 2
    track.scrollTo({ left, behavior: 'smooth' })
  }, [active])

  return (
    <div ref={trackRef} className={cn('no-scrollbar -mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0', className)}>
      <div role="tablist" aria-label={ariaLabel} className={cn('gap-1 rounded-xl p-1', fill ? 'flex w-full' : 'inline-flex min-w-max', styles.track)}>
        {tabs.map((tab) => {
          const isActive = tab.key === active
          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              id={`${idPrefix}-${tab.key}`}
              aria-selected={isActive}
              aria-controls={isActive ? panelId : undefined}
              data-testid={`${testIdPrefix}${tab.key}`}
              onClick={() => onSelect(tab.key)}
              className={cn(
                'min-h-10 whitespace-nowrap rounded-lg px-4 font-body text-sm font-semibold transition-[background-color,color,box-shadow] duration-200',
                // A tutta larghezza le tab si dividono la riga: sotto sm meno respiro laterale, così "Palinsesto/Schedine/Classifica" entrano a 320 px.
                fill && 'min-h-11 flex-1 px-2 sm:px-4',
                isActive ? styles.active : styles.idle,
              )}
            >
              {tab.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}
